import i18n from 'i18next';

/**
 * Checks if the current language is a right-to-left (RTL) language.
 * 
 * This function uses the current language from the i18next instance and compares it 
 * against a predefined list of RTL languages. If the current language is in the list,
 * the function returns true, indicating that the language is RTL. Otherwise, it returns false.
 * 
 * @returns {boolean} True if the current language is RTL, otherwise false.
 */
export const isRTL= () => {
    const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];
    const currentLanguage = i18n.language; // get current language from i18n
    return RTL_LANGUAGES.includes(currentLanguage);
};



/**
 * Truncates a string to a specified maximum number of words and/or characters.
 * 
 * @param str - The string to be truncated.
 * @param maxWords - The maximum number of words allowed in the truncated string.
 * @param maxChars - The maximum number of characters allowed in the truncated string.
 * @returns The truncated string with an ellipsis appended if truncation occurs, otherwise the original string.
 */
export function truncateText(str: string, maxWords: number, maxChars: number): string {
    // Split the string into words
    const words: string[] = str.split(" ");

    // Initialize variables to keep track of the total character count and the truncated string
    let charCount = 0;
    let truncatedString = "";

    // Iterate through each word in the string
    for (let i = 0; i < words.length; i++) {
        // Check if adding the next word would exceed the character limit or word limit
        if (charCount + words[i].length + (i > 0 ? 1 : 0) > maxChars || i >= maxWords) {
            // If either limit is reached, append an ellipsis and return the truncated string
            return truncatedString.trim() + "...";
        }

        // Add the current word to the truncated string
        // Add a space before the word if it's not the first word
        truncatedString += (i > 0 ? " " : "") + words[i];

        // Update the character count (including space if it's not the first word)
        charCount += words[i].length + (i > 0 ? 1 : 0);
    }

    // If the loop completes without hitting any limits, return the original or truncated string
    return truncatedString;
}


/**
 * Truncates a string to a specified maximum number of words.
 * 
 * @param str - The string to be truncated.
 * @param maxWords - The maximum number of words allowed in the truncated string.
 * @returns The truncated string with an ellipsis appended if truncation occurs, otherwise the original string.
 */
export function truncateWords(str: string, maxWords: number): string {
    // Split the string into words
    const words: string[] = str.split(" ");

    // If the number of words is less than or equal to the maximum allowed words,
    // return the original string
    if (words.length <= maxWords) {
        return str;
    }

    // Otherwise, join the first maxWords words and append an ellipsis
    return words.slice(0, maxWords).join(" ") + "...";
}


export const copyToClipBoard = async (copyMe: string) => {
    try {
        await navigator.clipboard.writeText(copyMe);
        return true;
    } catch (err) {
        return false
    }
};


export const capitalize = (title: string) => {
    return title.charAt(0).toUpperCase() + title.slice(1);
}


/**
 * Converts an object into URL parameters string, removing null values.
 * @param obj The object containing key-value pairs to be converted.
 * @returns A string containing URL parameters.
 */
export function getUrlParams(obj: Record<string, any>): string {
    const params = new URLSearchParams();

    for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== null) {
            params.append(key, obj[key]);
        }
    }

    return params.toString();
}


export function getRandomObjectsArray(subcategories: any[]): any[] {
    const length: number = subcategories.length; // Cache the length of the array

    // Generate two random indexes within the range of the array length
    const index1: number = Math.floor(Math.random() * length);
    let index2: number = Math.floor(Math.random() * length);

    // Ensure index2 is not the same as index1
    while (index2 === index1) {
        index2 = Math.floor(Math.random() * length);
    }

    // Return the two randomly selected array
    return [subcategories[index1], subcategories[index2]];
}


export const formatDate = (dateString: string, lang: string = "en") => {
    const date = new Date(dateString);
  
    return date.toLocaleDateString(lang, {
      weekday: "short", // Wed
      month: "short",   // Jun
      day: "numeric",   // 4
      year: "numeric",  // 2025
    });
  };